import { useState, useMemo } from 'react'; // Removed useEffect
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../config/api';
import PassportConnectButton from '../components/PassportConnectButton';
import { motion } from 'framer-motion';
import {
  VideoCameraIcon,
  CalendarIcon,
  TagIcon,
  PhotoIcon,
  PlayIcon,
  ClockIcon,
  SparklesIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

const CreateStream = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // user object now contains the token
  const [loading, setLoading] = useState(false);
  // We only support Twitch now, so this is a constant
  const streamSource = 'twitch';
  const [twitchChannelLink, setTwitchChannelLink] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledStartTime, setScheduledStartTime] = useState('');

  // Calculate min datetime for schedule input (current time + 5 minutes)
  const minScheduleTime = useMemo(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // Add 5 minutes buffer
    // Format to YYYY-MM-DDTHH:mm required by datetime-local
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }, []);


  // No longer need to generate a stream key since we're only using Twitch

  const handleCreateStream = async (e) => {
    e.preventDefault();
    // Check for user connection AND token availability
    if (!user?.isConnected || !user?.token) {
      toast.error('Authentication token not found. Please login again.');
      return;
    }

    setLoading(true); // Show loading indicator during submission

    const payload = {
      title: e.target.title.value,
      description: e.target.description.value,
      category: e.target.category.value,
      game: e.target.category.value, // Assuming category is the game for now
      tags: e.target.tags.value.split(',').map(tag => tag.trim()).filter(tag => tag), // Filter empty tags
      streamSource: streamSource,
      thumbnailUrl: thumbnailUrl || undefined, // Send only if not empty
      status: isScheduled ? 'scheduled' : 'live',
    };

    if (isScheduled) {
      if (!scheduledStartTime) {
        toast.error('Please select a scheduled start time.');
        setLoading(false);
        return;
      }
      // Validate schedule time is in the future
      if (new Date(scheduledStartTime) <= new Date()) {
        toast.error('Scheduled time must be in the future.');
        setLoading(false);
        return;
      }
      payload.scheduledStartTime = scheduledStartTime;
    }


    // Only Twitch is supported now
    if (!twitchChannelLink) {
      toast.error('Please enter your Twitch channel link.');
      setLoading(false);
      return;
    }
    // Basic validation for Twitch link (can be improved)
    if (!twitchChannelLink.toLowerCase().includes('twitch.tv/')) {
        toast.error('Please enter a valid Twitch channel link (e.g., https://twitch.tv/yourchannel).');
        setLoading(false);
        return;
    }
    payload.twitchChannelLink = twitchChannelLink;


    try {
      const response = await fetch(`${API_BASE_URL}/api/streams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${user.token}` // Use token from context
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create stream' }));
        throw new Error(errorData.message || 'Failed to create stream');
      }

      const data = await response.json();
      console.log('Received data after stream creation:', data); // Log the received data
      toast.success('Stream created successfully!');

      // Check if streamId exists before navigating
      if (data && data.streamId) {
        navigate(`/live/${data.streamId}`);
      } else {
        console.error('Stream ID not found in response data:', data);
        toast.error('Failed to get stream ID after creation.');
      }
    } catch (error) {
      console.error('Error creating stream:', error);
      toast.error(`Failed to create stream: ${error.message}`);
    } finally {
      setLoading(false); // Hide loading indicator
    }
  };

  if (!user?.isConnected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto"
        >
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <VideoCameraIcon className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Login Required</h2>
            <p className="text-gray-400 mb-6">Please login to start creating amazing streams</p>
            <PassportConnectButton className="w-full" />
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="relative">
            <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-pink-500/30 border-b-pink-500 rounded-full animate-spin mx-auto" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>
          <p className="text-gray-400 mt-6 text-lg">Creating your stream...</p>
          <p className="text-gray-500 mt-2">This won't take long</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/10 to-gray-900">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header Section */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl mb-6"
            >
              <RocketLaunchIcon className="w-10 h-10 text-white" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl font-bold text-white mb-4"
            >
              Create Your Stream
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-gray-400 max-w-2xl mx-auto"
            >
              Set up your live stream and start engaging with your audience. Connect your Twitch channel and customize your stream details.
            </motion.p>
          </div>

          {/* Main Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 p-6 border-b border-gray-700/50">
              <div className="flex items-center gap-3">
                <SparklesIcon className="w-6 h-6 text-purple-400" />
                <h2 className="text-xl font-semibold text-white">Stream Configuration</h2>
              </div>
              <p className="text-gray-400 mt-2">Fill in the details below to set up your stream</p>
            </div>

            <div className="p-8">
              <form onSubmit={handleCreateStream} className="space-y-8">
                {/* Twitch Channel Link */}
                <div className="space-y-3">
                  <label htmlFor="twitchChannelLink" className="flex items-center gap-2 text-sm font-medium text-gray-300">
                    <VideoCameraIcon className="w-4 h-4 text-purple-400" />
                    Twitch Channel Link
                    <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      id="twitchChannelLink"
                      name="twitchChannelLink"
                      required
                      value={twitchChannelLink}
                      onChange={(e) => setTwitchChannelLink(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                      placeholder="https://twitch.tv/yourchannel"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">Connect your Twitch channel to start streaming</p>
                </div>

                {/* Stream Title */}
                <div className="space-y-3">
                  <label htmlFor="title" className="flex items-center gap-2 text-sm font-medium text-gray-300">
                    <PlayIcon className="w-4 h-4 text-purple-400" />
                    Stream Title
                    <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    required
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter an engaging stream title"
                  />
                  <p className="text-xs text-gray-500">Make it catchy to attract more viewers</p>
                </div>

                {/* Description */}
                <div className="space-y-3">
                  <label htmlFor="description" className="flex items-center gap-2 text-sm font-medium text-gray-300">
                    <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                    </svg>
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Tell your audience what your stream is about..."
                  />
                  <p className="text-xs text-gray-500">Describe what viewers can expect from your stream</p>
                </div>

                {/* Category and Tags Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label htmlFor="category" className="flex items-center gap-2 text-sm font-medium text-gray-300">
                      <TagIcon className="w-4 h-4 text-purple-400" />
                      Category
                      <span className="text-red-400">*</span>
                    </label>
                    <select
                      id="category"
                      name="category"
                      required
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="gaming">🎮 Gaming</option>
                      <option value="esports">🏆 Esports</option>
                      <option value="crypto">₿ Crypto</option>
                      <option value="other">🌟 Other</option>
                    </select>
                  </div>

                  <div className="space-y-3">
                    <label htmlFor="tags" className="flex items-center gap-2 text-sm font-medium text-gray-300">
                      <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Tags
                    </label>
                    <input
                      type="text"
                      id="tags"
                      name="tags"
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                      placeholder="gaming, crypto, esports"
                    />
                    <p className="text-xs text-gray-500">Separate tags with commas</p>
                  </div>
                </div>

                {/* Thumbnail URL */}
                <div className="space-y-3">
                  <label htmlFor="thumbnailUrl" className="flex items-center gap-2 text-sm font-medium text-gray-300">
                    <PhotoIcon className="w-4 h-4 text-purple-400" />
                    Thumbnail URL
                    <span className="text-xs text-gray-500 ml-1">(Optional)</span>
                  </label>
                  <input
                    type="url"
                    id="thumbnailUrl"
                    name="thumbnailUrl"
                    value={thumbnailUrl}
                    onChange={(e) => setThumbnailUrl(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    placeholder="https://example.com/your-thumbnail.png"
                  />
                  <p className="text-xs text-gray-500">Add a custom thumbnail to make your stream stand out</p>
                </div>

                {/* Scheduling Section */}
                <div className="bg-gray-900/30 rounded-xl p-6 border border-gray-700/50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="w-5 h-5 text-purple-400" />
                      <div>
                        <h3 className="text-lg font-medium text-white">Schedule Options</h3>
                        <p className="text-sm text-gray-400">Choose when to start your stream</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="scheduleToggle"
                        checked={isScheduled}
                        onChange={(e) => setIsScheduled(e.target.checked)}
                        className="w-5 h-5 text-purple-600 bg-gray-800 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <label htmlFor="scheduleToggle" className="ml-3 text-gray-300 font-medium">
                        Schedule for Later
                      </label>
                    </div>
                  </div>

                  {isScheduled && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3"
                    >
                      <label htmlFor="scheduledStartTime" className="flex items-center gap-2 text-sm font-medium text-gray-300">
                        <ClockIcon className="w-4 h-4 text-purple-400" />
                        Scheduled Start Time
                        <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        id="scheduledStartTime"
                        name="scheduledStartTime"
                        required={isScheduled}
                        value={scheduledStartTime}
                        onChange={(e) => setScheduledStartTime(e.target.value)}
                        min={minScheduleTime}
                        className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                      />
                      <p className="text-xs text-gray-500">Your stream will be scheduled and viewers can set reminders</p>
                    </motion.div>
                  )}
                </div>


                {/* Submit Button */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg hover:shadow-purple-500/25"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Creating Stream...
                      </>
                    ) : (
                      <>
                        <RocketLaunchIcon className="w-5 h-5" />
                        Create Stream
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-gray-500 mt-3">
                    By creating a stream, you agree to our terms of service
                  </p>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateStream;
